import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';
import { usePublicForm } from '../hooks/usePublicContent';
import { submitLead } from '../services/leadService';
import { readOrigin } from '../utils/utm';
import { DynamicForm, DynamicFormResult } from '../components/public/DynamicForm';
import { Button } from '../components/ui/Button';
import { BrandLogo } from '../components/shared/BrandLogo';

/** Formulario público independiente: /formulario/:slug */
export function PublicForm() {
  const {
    slug = ''
  } = useParams();
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = usePublicForm(slug);
  const form = db.forms.find(f => f.slug === slug);
  const organization = form ? db.organizations.find(o => o.id === form.organizationId) : undefined;
  const logoUrl = organization?.brand.logoUrl ?? organization?.logoUrl;
  if (loading && !form) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-surface" aria-busy="true" data-id="element-1588" data-mp="3qt1vviy5qvjp">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1589" data-mp="3lyegc9l4tbh9" />
        <span className="sr-only" data-id="element-1590" data-mp="3gys15ulqtl5t">Cargando el formulario…</span>
      </main>;
  }
  if (!form) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-1591" data-mp="3c08f7u75g9dg">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-1592" data-mp="8gluwgfewv15" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-1593" data-mp="8jht85v69lq4">
          Formulario no disponible
        </h1>
        <Link to="/" data-id="element-1594" data-mp="3uwq3g1mshr4y">
          <Button variant="secondary" data-id="element-1595" data-mp="3ux9mjlo6elkk">Volver al inicio</Button>
        </Link>
      </main>;
  }
  async function handleSubmit(result: DynamicFormResult) {
    if (!form) return;
    await submitLead({
      organizationId: form.organizationId,
      formId: form.id,
      contact: result.contact,
      answers: result.answers,
      origin: readOrigin(location.search, {
        formId: form.id
      })
    });
    navigate(`/gracias/${form.thankYouSlug ?? form.slug}`);
  }
  return <main className="min-h-screen w-full bg-surface px-5 py-16 sm:py-24" data-id="element-1596" data-mp="2by1wb2e92wjq">
      <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-1597" data-mp="2bvxwavjh2hoq">
        <div className="mb-10 flex justify-center" data-id="element-1598" data-mp="2gw3gxrrbr3xb">
          <BrandLogo src={logoUrl} size={30} data-id="element-1599" data-mp="2gusf3xhfw68u" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-1600" data-mp="22wkgll4r8wvk">
          {form.name}
        </h1>
        {form.description && <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-1601" data-mp="1sz641wlycs8j">
            {form.description}
          </p>}
      </div>
      <DynamicForm form={form} onSubmit={handleSubmit} data-id="element-1602" data-mp="1xxhdw7c8z2qr" />
    </main>;
}